import type {
	CancelOptions,
	ZuoraPaymentMethod,
	ZuoraPaymentMethodSignatureSource,
} from '@kimuno/api-sdk/lib/services/billing';
import type { PaymentPeriod } from '@kimuno/api-sdk/lib/types/common';
import type {
	CheckEmailPayload,
	CheckoutPlan,
	CreateCheckoutSessionPayload,
	ResubscribeCheckoutSessionPayload,
	RevocationIntakePayload,
	SubmitPauseRequestPayload,
	UpdateCheckoutSessionPayload,
	UpgradePlanPayload,
} from '@kimuno/api-sdk/lib/types/requests/Billing';
import config from 'config';

import api from '../clients/api';
import { handleUnauthorized } from '../utils/apiWrapper';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';
import { getUserId } from './user.service';

const service = {
	cancelSubscription,
	cancelStripeSubscription,
	getBraintreeSignature,
	getLicenceById,
	getSignature,
	updatePaymentPeriod,
	getInvoices,
	getInvoiceDownloadLink,
	getSubscription,
	getSubscriptionDetails,
	getDiscountInformation,
	getActiveLicences,
	getSubscriptionUpgradePreview,
	addTrialDiscount,
	createPortalSession,
	createCheckoutSession,
	updateCheckoutSession,
	getCheckoutStatus,
	checkEmail,
	getUpgradeOptions,
	getUpgradePreview,
	upgradePlan,
	resubscribeCheckoutSession,
	submitRevocation,
	submitPauseRequest,
};

function cancelSubscription(userId: string, cancelOptions?: CancelOptions) {
	notifyBugsnagNetwork('billing', 'cancelSubscription');
	const options: CancelOptions = cancelOptions ?? {};
	return handleUnauthorized(() => api.billing.cancelSubscription(userId, options));
}

function cancelStripeSubscription(reason?: string) {
	notifyBugsnagNetwork('billing', 'cancelStripeSubscription');
	return handleUnauthorized(() => api.billing.cancelStripeSubscription({ reason }));
}

function getInvoices(userId: string) {
	notifyBugsnagNetwork('billing', 'getInvoices');
	return handleUnauthorized(() => api.billing.getInvoices(userId));
}

function getInvoiceDownloadLink(userId: string, invoiceId: string) {
	notifyBugsnagNetwork('billing', 'getInvoiceDownloadLink');
	return handleUnauthorized(() => api.billing.getInvoiceDownloadLink(userId, invoiceId));
}

function getBraintreeSignature() {
	notifyBugsnagNetwork('billing', 'getBraintreeSignature');
	return handleUnauthorized(() => api.billing.getBraintreeSignature());
}

function getSignature(method: ZuoraPaymentMethod, source?: string) {
	notifyBugsnagNetwork('billing', 'getSignature');

	// Payment page id — defaults to the configured Zuora payment source
	const _source = (source || config.zuoraPaymentSource) as ZuoraPaymentMethodSignatureSource;

	return handleUnauthorized(() => api.billing.getSignature(method, _source));
}

function getLicenceById(licenceModelId: number) {
	return api.billing.getLicenceById(licenceModelId);
}

function updatePaymentPeriod(paymentRhythm: PaymentPeriod) {
	notifyBugsnagNetwork('billing', 'updatePaymentPeriod');
	return handleUnauthorized(() => api.billing.updatePaymentPeriod(getUserId(), paymentRhythm));
}

function getSubscription(userId: string) {
	notifyBugsnagNetwork('billing', 'getSubscription');
	return handleUnauthorized(() => api.billing.getSubscription(userId));
}

// SM-151. Reads the current user's Stripe subscription details (kimuno's
// webhook-reconciled shadow state). NEU uses `subscription.isCanceled` to
// decide whether to show the Cancel Subscription button on My Profile.
function getSubscriptionDetails() {
	notifyBugsnagNetwork('billing', 'getSubscriptionDetails');
	return handleUnauthorized(() => api.billing.getSubscriptionDetails());
}

function getDiscountInformation(promoCode: string, licenceId: number) {
	notifyBugsnagNetwork('billing', 'getDiscountInformation');
	return handleUnauthorized(() => api.billing.getDiscountInformation(promoCode, licenceId));
}

function getActiveLicences() {
	return handleUnauthorized(() => api.billing.getActiveLicences());
}

function getSubscriptionUpgradePreview(userId: string, licenceModelId: number) {
	notifyBugsnagNetwork('billing', 'getSubscriptionUpgradePreview');
	return handleUnauthorized(() => api.user.getSubscriptionUpgradePreview(userId, licenceModelId));
}

function addTrialDiscount(userId: string) {
	notifyBugsnagNetwork('billing', 'addTrialDiscount');
	return handleUnauthorized(() => api.billing.addTrialDiscount(userId));
}

function createPortalSession(returnUrl: string) {
	notifyBugsnagNetwork('billing', 'createPortalSession');
	return handleUnauthorized(() => api.billing.createPortalSession(returnUrl));
}

function createCheckoutSession(payload: CreateCheckoutSessionPayload) {
	notifyBugsnagNetwork('billing', 'createCheckoutSession');
	return handleUnauthorized(() => api.billing.createCheckoutSession(payload));
}

// SM-284. Anonymous (no account yet), like createCheckoutSession/getCheckoutStatus
// — called directly, not through handleUnauthorized.
function updateCheckoutSession(sessionId: string, payload: UpdateCheckoutSessionPayload) {
	notifyBugsnagNetwork('billing', 'updateCheckoutSession');
	return api.billing.updateCheckoutSession(sessionId, payload);
}

// The checkout-status endpoint is anonymous (the user has no account yet during
// onboarding), so we call it directly rather than through handleUnauthorized.
function getCheckoutStatus(sessionId: string) {
	notifyBugsnagNetwork('billing', 'getCheckoutStatus');
	return api.billing.getCheckoutStatus(sessionId);
}

// The check-email endpoint is anonymous (fired before the user has an account),
// so we call it directly rather than through handleUnauthorized — same pattern
// as getCheckoutStatus above.
function checkEmail(payload: CheckEmailPayload) {
	notifyBugsnagNetwork('billing', 'checkEmail');
	return api.billing.checkEmail(payload);
}

// SM-162. List the user's plan-upgrade options. Kimuno derives the current
// plan (Stripe paid-tier or 'basis') and returns one card per allow-listed
// upgrade target with its per-month Stripe price.
function getUpgradeOptions() {
	notifyBugsnagNetwork('billing', 'getUpgradeOptions');
	return handleUnauthorized(() => api.billing.getUpgradeOptions());
}

// SM-160. Apply a plan upgrade to the user's Stripe subscription. The 200
// response is intentionally empty — NEU soft-polls `fetchMe()` afterwards to
// confirm the new tier once the webhook entitlement mirror has caught up.
function upgradePlan(payload: UpgradePlanPayload) {
	notifyBugsnagNetwork('billing', 'upgradePlan');
	return handleUnauthorized(() => api.billing.upgradePlan(payload));
}

// SM-162 Stage 2. Per-target upgrade preview. Kimuno calls
// `invoices.createPreview` and returns the exact proration ("Heute fällige
// Summe"), the new monthly amount, the period end date, and the saved default
// payment method (type + last4). Backs the NEU confirmation page.
function getUpgradePreview(targetPlanKey: CheckoutPlan) {
	notifyBugsnagNetwork('billing', 'getUpgradePreview');
	return handleUnauthorized(() => api.billing.getUpgradePreview(targetPlanKey));
}

// SM-202. Create a Stripe Embedded Checkout session for a basis-with-Stripe
// user to resubscribe to a paid plan. Kimuno resolves the user's existing
// Stripe customer (no email collected) and skips the trial branch — basis
// users have already used their trial.
function resubscribeCheckoutSession(payload: ResubscribeCheckoutSessionPayload) {
	notifyBugsnagNetwork('billing', 'resubscribeCheckoutSession');
	return handleUnauthorized(() => api.billing.resubscribeCheckoutSession(payload));
}

// SM-117. Submit a 14-day right-of-withdrawal (Widerruf) revocation request for
// the current user's Stripe subscription. The proxy injects the user id from the
// auth context, so only an optional free-text `reason` is sent. Kimuno records a
// pending request that customer support later executes (immediate cancel + full
// refund) — NEU no longer routes Stripe withdrawals through the WIDERRUF
// experiment hack + support email.
function submitRevocation(payload: RevocationIntakePayload) {
	notifyBugsnagNetwork('billing', 'submitRevocation');
	return handleUnauthorized(() => api.billing.submitRevocation(payload));
}

// SM-411. Submit a customer-side pause request for the current user's Stripe
// subscription. The proxy injects the user id from the auth context, so only
// the picked duration (1 / 2 / 3 months) is sent. Kimuno records a pending
// PauseRequests row that customer support later approves or rejects — Stripe is
// only touched at admin-approve time (SM-412).
function submitPauseRequest(payload: SubmitPauseRequestPayload) {
	notifyBugsnagNetwork('billing', 'submitPauseRequest');
	return handleUnauthorized(() => api.billing.submitPauseRequest(payload));
}

export default service;
